import ErrorHandler from '@schachermayer/sch-vuejs-commons/src/errors';
import { Action, Module, Mutation, MutationAction, VuexModule } from 'vuex-module-decorators';
import { BasketApi } from '../../../build/swagger-code-extranet-api-definition/apis/basket-api';
import { BudgetApi } from '../../../build/swagger-code-extranet-api-definition/apis/budget-api';
import { MenuEntriesApi } from '../../../build/swagger-code-extranet-api-definition/apis/menu-entries-api';
import {
    ApplicationBarStandalone,
    ApplicationBarStandaloneRequest,
    BasketSummaryForAllBaskets,
    BasketType,
    BudgetCheckResponse,
    BudgetOrdersSummary,
    Company,
    MenuEntry,
} from '../../../build/swagger-code-extranet-api-definition/models';
import { ApplicationBarMenuEntryMapper } from './ApplicationBarMenuEntryMapper';
import { ApplicationInfoApi } from '../../../build/swagger-code-extranet-api-definition/apis/application-info-api';
import {
    ApplicationBarStandaloneApi,
} from '../../../build/swagger-code-extranet-api-definition/apis/application-bar-standalone-api';

const basketApi: BasketApi = new BasketApi({ basePath: process.env.VUE_APP_CONTEXT_BASE });
const budgetApi: BudgetApi = new BudgetApi({ basePath: process.env.VUE_APP_CONTEXT_BASE });
const menuEntriesApi: MenuEntriesApi = new MenuEntriesApi({ basePath: process.env.VUE_APP_CONTEXT_BASE });
const applicationBarStandaloneApi: ApplicationBarStandaloneApi = new ApplicationBarStandaloneApi({ basePath: process.env.VUE_APP_CONTEXT_BASE });
const applicationInfoApi: ApplicationInfoApi = new ApplicationInfoApi({ basePath: process.env.VUE_APP_CONTEXT_BASE });

@Module({
    namespaced: true,
    name: 'applicationBar',
})
export default class ApplicationBarStoreModule extends VuexModule {

    menuEntries: MenuEntry[] = [];
    accountEntries: MenuEntry[] = [];

    budgetOrdersSummary: BudgetOrdersSummary = {};
    basketSummaryForAllBaskets: BasketSummaryForAllBaskets = {};
    isBasketSummaryLoading: boolean = false;

    addedBasketItems: BasketItem[] = [];

    budgetCheckResponse: BudgetCheckResponse = null;

    company: Company | null = null;

    applicationBarStandalone: ApplicationBarStandalone = {};

    @Mutation
    addToBasketInApplicationBar(basketItem: BasketItem) {
        this.addedBasketItems.push(basketItem);
    }

    @Mutation
    addToBasketSummary({ price, net }) {
        const standardBasket = this.basketSummaryForAllBaskets.basketSummaries.filter((basket) => basket.basketType === BasketType.Standard)[0];
        standardBasket.positionsCount++;
        if (net) {
            standardBasket.sumNettoPrice.priceNetto += price;
        } else {
            standardBasket.sumNettoPrice.priceGross += price;
        }
    }

    @Mutation
    storeBudgetOrdersSummary(budgetOrdersSummary: BudgetOrdersSummary) {
        this.budgetOrdersSummary = budgetOrdersSummary;
    }

    @Mutation
    storeBasketSummaryForAllBaskets(basketSummaryForAllBaskets: BasketSummaryForAllBaskets) {
        this.basketSummaryForAllBaskets = basketSummaryForAllBaskets;
        this.isBasketSummaryLoading = false;
    }

    @Mutation
    storeBudgetCheckResponse(budgetCheckResponse: BudgetCheckResponse) {
        this.budgetCheckResponse = budgetCheckResponse;
    }

    @Mutation
    storeBasketSummaryLoading() {
        this.isBasketSummaryLoading = true;
    }

    @Action({ commit: 'storeBasketSummaryForAllBaskets' })
    async loadBasketSummaryForAllBaskets(): Promise<BasketSummaryForAllBaskets> {
        try {
            this.context.commit('storeBasketSummaryLoading');
            return (await basketApi.getBasketSummaryForAllBaskets({ credentials: 'same-origin' })).data;
        } catch (error) {
            await ErrorHandler.handleError(error);
            return {};
        }
    }

    @Action({ commit: 'storeBudgetOrdersSummary', rawError: true })
    async loadBudgetOrdersSummary(): Promise<BudgetOrdersSummary> {
        try {
            return (await budgetApi.getBudgetOrdersSummary({ credentials: 'same-origin' })).data;
        } catch (error) {
            await ErrorHandler.handleError(error);
            return {};
        }
    }

    @Action({ commit: 'storeBudgetCheckResponse', rawError: true })
    async checkBudget(): Promise<BudgetCheckResponse> {
        try {
            return (await budgetApi.checkBudget({ credentials: 'same-origin' })).data;
        } catch (error) {
            await ErrorHandler.handleError(error);
            return {};
        }
    }

    @MutationAction({ mutate: ['accountEntries'], rawError: true })
    async loadAccountEntries() {
        try {
            const accountEntries: MenuEntry[] = (await menuEntriesApi.getAccountEntries({ credentials: 'same-origin' })).data;
            return { accountEntries };
        } catch (error) {
            await ErrorHandler.handleError(error);
            return { accountEntries: {} };
        }
    }

    @MutationAction({ mutate: ['menuEntries'], rawError: true })
    async loadMenuEntries(params: any) {
        try {
            const apiMenuEntries: MenuEntry[] = (await menuEntriesApi.getMenuEntries({ credentials: 'same-origin' })).data;
            const menuEntries = ApplicationBarMenuEntryMapper.mapMenuEntryUrls(apiMenuEntries, params);
            return { menuEntries };
        } catch (error) {
            await ErrorHandler.handleError(error);
            return { menuEntries: {} };
        }
    }

    @MutationAction({ mutate: ['applicationBarStandalone'], rawError: true })
    async loadApplicationBarStandalone(applicationBarStandaloneRequest: ApplicationBarStandaloneRequest) {
        try {
            const applicationBarStandalone: ApplicationBarStandalone = (await applicationBarStandaloneApi.getApplicationBarStandalone(
                applicationBarStandaloneRequest,
                { credentials: 'same-origin' },
            )).data;
            return { applicationBarStandalone };
        } catch (error) {
            await ErrorHandler.handleError(error);
            return { applicationBarStandalone: {} };
        }
    }

    @Action
    public async refreshApplicationBarBasket(): Promise<void> {
        try {
            if (
                this.applicationBarStandalone.enabled === true &&
                this.applicationBarStandalone.content !== undefined
            ) {
                // @ts-ignore: function is defined in applicationBar directly
                updateApplicationBarBasket();
                // @ts-ignore: function is defined in applicationBar directly
                updateApplicationBarNotifications();
            } else {
                await this.loadBasketSummaryForAllBaskets();
                await this.loadBudgetOrdersSummary();
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-implicit-any-catch
        } catch (err) {
            await ErrorHandler.handleError(err);
        }
    }

    @MutationAction({ mutate: ['company'], rawError: true })
    public async loadCompany() {
        try {
            const company = (await applicationInfoApi.getCompany({ credentials: 'same-origin' })).data;
            return { company };
        } catch (error) {
            await ErrorHandler.handleError(error);
            return { company: null };
        }
    }

}

export interface BasketItem {
    fromTop: number;
    fromLeft: number;
    price: number;
}
